import { Box } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { InvestorDashboardBorrowerStatuses } from '../../../../../constants/investorDashboardStatus';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import SelectWrapper from '../../../../../shared/components/formUtilities/useSelect';
import DataTableGrid from '../../../../../shared/components/formUtilities/useTable';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { InvestorDashboardService } from '../investorDashboardService';
import { InitializeInvestorBorrowersColumns } from './borrowersColumns';

export const InvestorDashboardBorrowers = () => {
	const { t } = useTranslation();
	const investorDashboardService = new InvestorDashboardService();
	const columns = InitializeInvestorBorrowersColumns(t);
	const [borrowers, setBorrowers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageCount: 0,
		perPage: 10,
		totalCount: 0,
	});

	const form = useFormik({
		initialValues: { businessName: '', nationalId: '', status: 'all' },
		enableReinitialize: true,
	});

	const requestIdRef = useRef(0);

	const getBorrowers = (currentPage = 1, perPage = pagination.perPage) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		investorDashboardService
			.getBorrowers(currentPage, perPage, form.values)
			.then((resp) => {
				if (requestId !== requestIdRef.current) return;
				setBorrowers(resp.data?.list || []);
				setPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: resp.data.paging?.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
			})
			.finally(() => {
				if (requestId === requestIdRef.current) setLoading(false);
			});
	};

	useEffect(() => {
		getBorrowers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onPageChange = (page) => getBorrowers(page + 1, pagination.perPage);
	const onPageSizeChange = (perPage) => getBorrowers(1, perPage);
	const onSearch = () => getBorrowers(1, pagination.perPage);

	return (
		<Box className="flex flex-col gap-4">
			<FormikProvider value={form}>
				<Box className="flex flex-wrap gap-3 items-start">
					<Box className="w-full md:w-[220px]">
						<InputWrapper label={t('businessName')} name="businessName" />
					</Box>
					<Box className="w-full md:w-[300px]">
						<InputWrapper label={t('nationalId')} name="nationalId" />
					</Box>
					<Box className="w-full md:w-[220px]">
						<SelectWrapper
							label={t('status')}
							name="status"
							showDefaultOption
							defaultOptionText={t('all')}
							defaultOptionValue="all"
							valueKey="id"
							labelKey="name"
							items={InvestorDashboardBorrowerStatuses}
						/>
					</Box>
					<Box className="flex flex-col items-start">
						<Box className="flex flex-0 items-center !relative w-full">
							<TextWrapper classList="invisible truncate-word" type="caption" text="&nbsp;" />
						</Box>
						<ButtonWrapper
							classList="!h-10"
							label={t('search')}
							onClick={onSearch}
							disabled={loading}
						/>
					</Box>
				</Box>
			</FormikProvider>
			<DataTableGrid
				columnPinningLeft={isMobile ? ['businessName'] : []}
				name="investorDashboardBorrowersTbl"
				loading={loading}
				onPageChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
				pagination={pagination}
				columns={columns}
				records={borrowers}
				isPagination
				showEditColumnsButton={false}
				headerCellClass="!px-4"
				bodyCellClass="!px-4 !py-3"
			/>
		</Box>
	);
};
